/**
 * Схема целевых колонок DWH для маппинга мастера импорта.
 *
 * Перенесено из старого `@/lib/dwh-schema.ts`. Используется ТОЛЬКО
 * import-wizard (компонент step-mapping и серверная логика), поэтому живёт
 * внутри модуля как доменные данные, а не в общем `@/lib`.
 */

// ============================================================
// DWH materialized view columns (backward compat with mv_scen1-7)
// ============================================================
export const DWH_COLUMNS = {
  wagonNumber: { key: 'wagon_number', dwhName: '"Номер вагона"', alias: 'wagon_number' },
  wagonNumberImport: { key: 'wagon_number_import', dwhName: '"Номер вагона Импорт"', alias: 'wagon_number_import' },
  cargoCode: { key: 'cargo_code', dwhName: '"Код груза ЕТСНГ"', alias: 'cargo_code' },
  cargoName: { key: 'cargo_name', dwhName: '"Груз"', alias: 'cargo_name' },
  importCountry: { key: 'import_country', dwhName: '"Страна отправления"', alias: 'import_country' },
  exportCountry: { key: 'export_country', dwhName: '"Страна назначения"', alias: 'export_country' },
  importStation: { key: 'import_station', dwhName: '"Станция назначения"', alias: 'import_station' },
  exportStation: { key: 'export_station', dwhName: '"Станция отправления"', alias: 'export_station' },
  importDate: { key: 'import_date', dwhName: '"Дата раскредитации"', alias: 'import_date' },
  exportDate: { key: 'export_date', dwhName: '"Дата приема груза к перевозке"', alias: 'export_date' },
  importWeight: { key: 'import_weight', dwhName: '"Масса груза Импорт"', alias: 'import_weight' },
  exportWeight: { key: 'export_weight', dwhName: '"Масса груза Экспорт"', alias: 'export_weight' },
  importBin: { key: 'import_bin', dwhName: '"БИН получателя"', alias: 'import_bin' },
  exportBin: { key: 'export_bin', dwhName: '"БИН отправителя"', alias: 'export_bin' },
  importCompany: { key: 'import_company', dwhName: '"Получатель"', alias: 'import_company' },
  exportCompany: { key: 'export_company', dwhName: '"Отправитель"', alias: 'export_company' },
  importPayer: { key: 'import_payer', dwhName: '"Плательщик Импорт"', alias: 'import_payer' },
  exportPayer: { key: 'export_payer', dwhName: '"Плательщик Экспорт"', alias: 'export_payer' },
  importPayerCode: { key: 'import_payer_code', dwhName: '"Кода плательщика Импорт"', alias: 'import_payer_code' },
  exportPayerCode: { key: 'export_payer_code', dwhName: '"Кода плательщика Экспорт"', alias: 'export_payer_code' },
  importActualPayment: { key: 'import_actual_payment', dwhName: '"Импорт фактическая оплата"', alias: 'import_actual_payment' },
  exportActualPayment: { key: 'export_actual_payment', dwhName: '"Экспорт фактическая оплата"', alias: 'export_actual_payment' },
  weightDiff: { key: 'weight_diff', dwhName: '"Разница веса"', alias: 'weight_diff' },
  daysBetween: { key: 'days_between', dwhName: '"Разница дат"', alias: 'days_between' },
  importDocument: { key: 'import_document', dwhName: '"Номер отправки Импорт"', alias: 'import_document' },
  exportDocument: { key: 'export_document', dwhName: '"Номер отправки Экспорт"', alias: 'export_document' },
  importMgsp: { key: 'import_mgsp', dwhName: '"МГСП Импорт"', alias: 'import_mgsp' },
  exportMgsp: { key: 'export_mgsp', dwhName: '"МГСП Экспорт"', alias: 'export_mgsp' },
} as const

export type DwhColumnKey = keyof typeof DWH_COLUMNS

export const IMPORT_TARGET_COLUMNS = Object.values(DWH_COLUMNS).map((col) => ({
  key: col.key,
  label: col.dwhName.replace(/"/g, ''),
  alias: col.alias,
  required: ['wagon_number', 'cargo_code', 'cargo_name', 'import_date', 'import_weight'].includes(col.key),
}))

// ============================================================
// Real source file columns (pilot data 58-col structure)
// Maps internal field keys to possible column name variations
// Used by the import wizard for auto-detecting columns via fuzzy matching
// ============================================================
export const REAL_COLUMNS = {
  wagonNumber: { key: 'wagon_number', aliases: ['Номер вагона\\конт', 'Номер вагона', 'wagon_number', 'Номер вагона Импорт'] },
  cargoCode: { key: 'cargo_code', aliases: ['Код груза', 'Код груза ЕТСНГ', 'cargo_code'] },
  cargoName: { key: 'cargo_name', aliases: ['Наименование груза', 'Груз', 'cargo_name'] },
  stationDest: { key: 'station_dest', aliases: ['Станция назначения', 'station_dest'] },
  stationDestName: { key: 'station_dest_name', aliases: ['Наименование станции назначения', 'Наименование станции назнаения'] },
  stationOrigin: { key: 'station_origin', aliases: ['Станция отправления', 'station_origin'] },
  stationOriginName: { key: 'station_origin_name', aliases: ['Наименование станции отправления'] },
  weight: { key: 'weight_kg', aliases: ['Вес на вагон (кг)', 'Общий вес по документу (кг)', 'weight_kg'] },
  dateSent: { key: 'date_sent', aliases: ['Дата отправления', 'date_sent'] },
  dateArrived: { key: 'date_arrived', aliases: ['Дата прибытия', 'date_arrived'] },
  dateIssued: { key: 'date_issued', aliases: ['Дата выдачи', 'date_issued'] },
  countryDest: { key: 'country_dest', aliases: ['Страна назначения', 'country_dest'] },
  countryDestName: { key: 'country_dest_name', aliases: ['Наименование страны назначения'] },
  countryOrigin: { key: 'country_origin', aliases: ['Страна отправления', 'country_origin'] },
  countryOriginName: { key: 'country_origin_name', aliases: ['Наименование страны отправления'] },
  payerCode: { key: 'payer_code', aliases: ['Код плательщика', 'payer_code'] },
  payerName: { key: 'payer_name', aliases: ['Наименование плательщика', 'payer_name'] },
  document: { key: 'document', aliases: ['Документ', 'document'] },
  messageType: { key: 'message_type', aliases: ['Вид сообщения (2,5-импорт)', 'Вид сообщения (1,4-экспорт)', 'Вид сообщения'] },
  senderCode: { key: 'sender_code', aliases: ['Код грузоотправителя'] },
  senderName: { key: 'sender_name', aliases: ['Наименование ГО'] },
  receiverCode: { key: 'receiver_code', aliases: ['Код грузополучателя'] },
  receiverName: { key: 'receiver_name', aliases: ['Наименование ГП'] },
  distance: { key: 'distance', aliases: ['Расстояние', 'distance'] },
  month: { key: 'month', aliases: ['Месяц', 'month'] },
} as const

export type RealColumnKey = keyof typeof REAL_COLUMNS

// Target fields for the import wizard column mapping UI
export const IMPORT_TARGET_FIELDS = Object.entries(REAL_COLUMNS).map(([, col]) => ({
  key: col.key,
  label: col.aliases[0], // Основное русское название
  aliases: col.aliases as readonly string[],
  required: ['wagon_number', 'cargo_code', 'cargo_name', 'weight_kg', 'date_sent', 'station_origin'].includes(col.key),
}))
